import {
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";

/**
 * Keeps a closing island mounted until its exit transition ends, inert and
 * showing the content it had while open (so "3 selected" doesn't read
 * "0 selected" on the way out). Without a running transition (jsdom, or a consumer
 * style that drops it) it unmounts right away, as it does when the transition
 * is canceled while it stays closed. Reopening mid-exit keeps it mounted and
 * the transition turns around.
 */
export const useCommandIslandPresence = (
    isOpen: boolean,
    children: ReactNode,
) => {
    const nodeRef = useRef<HTMLDivElement | null>(null);
    const isOpenRef = useLatestRef(isOpen);
    const [isMounted, setIsMounted] = useState(isOpen);
    if (isOpen && !isMounted) {
        setIsMounted(true);
    }
    const [openChildren, setOpenChildren] = useState(children);
    if (isOpen && children !== openChildren) {
        setOpenChildren(children);
    }

    useLayoutEffect(() => {
        if (nodeRef.current) {
            nodeRef.current.inert = !isOpen;
        }
    }, [isOpen, isMounted]);

    useEffect(() => {
        const node = nodeRef.current;
        if (isOpen || !isMounted) {
            return undefined;
        }
        const transitions = node?.getAnimations?.() ?? [];
        if (transitions.length === 0) {
            setIsMounted(false);
            return undefined;
        }
        let isCurrent = true;
        // A reopen reverses the transition, which cancels it too: only a
        // still-closed island unmounts.
        const unmount = () => {
            if (isCurrent && !isOpenRef.current) {
                setIsMounted(false);
            }
        };
        Promise.all(transitions.map(({ finished }) => finished)).then(
            unmount,
            unmount,
        );
        return () => {
            isCurrent = false;
        };
    }, [isOpen, isMounted, isOpenRef]);

    return {
        nodeRef,
        isMounted,
        shownChildren: isOpen ? children : openChildren,
    };
};
